import type { StudioRuleComponent, StudioRuleEntry } from './useExistingStudioRules.ts';

/** Trigger fired when a page's status changes; carries the target statuses in `value.pageStatuses`. */
const PAGE_STATUS_CHANGED_TRIGGER_TYPE = 'confluence.page-status-changed.trigger';
/** Trigger fired when a page is published. */
const PAGE_PUBLISHED_TRIGGER_TYPE = 'confluence.page-published.trigger';
/** Action that runs a Rovo agent (the Content Reviewer agent, for our purposes). */
const INVOKE_AGENT_ACTION_TYPE = 'rovo.invoke-agent.action';
/** Action that sends a templated email (the meeting-notes recap). */
const SEND_TEMPLATED_EMAIL_ACTION_TYPE = 'action.send-templated-email';
/** Branch that runs its children for each inactive (stale) page. */
const STALE_PAGE_BRANCH_TYPE = 'confluence.for-each-stale-page.branch';
/** Action that archives a page. */
const ARCHIVE_PAGE_ACTION_TYPE = 'confluence.archive-page.action';
/** Prefix shared by every Confluence trigger/action/condition type id. */
const CONFLUENCE_COMPONENT_TYPE_PREFIX = 'confluence.';
const ENABLED_STATE = 'enabled';
/** The page status that triggers the Content Reviewer nudge. */
const READY_FOR_REVIEW = 'ready for review';
/** Text a meeting-notes rule's name/description is expected to contain. */
const MEETING_NOTES_TEXT = 'meeting notes';

const equalsIgnoreCase = (value: string | undefined, expectedLower: string): boolean =>
	typeof value === 'string' && value.toLowerCase() === expectedLower;

const isRule = (rule: StudioRuleEntry | null | undefined): rule is StudioRuleEntry =>
	rule != null && typeof rule === 'object';

/** True when a component of the given type appears in the components or their `children`, at any depth. */
const hasComponentOfType = (
	components: StudioRuleComponent[] | undefined,
	typeLower: string,
): boolean =>
	Array.isArray(components) &&
	components.some(
		(component) =>
			equalsIgnoreCase(component?.type, typeLower) ||
			hasComponentOfType(component?.children, typeLower),
	);

/** True when the rule's trigger is of the given type. */
const hasTriggerType = (rule: StudioRuleEntry, typeLower: string): boolean =>
	equalsIgnoreCase(rule.trigger?.type, typeLower);

/** True when the rule is triggered by a page-status change to "Ready for review". */
const triggersOnReadyForReview = (rule: StudioRuleEntry): boolean => {
	if (!hasTriggerType(rule, PAGE_STATUS_CHANGED_TRIGGER_TYPE)) {
		return false;
	}
	const statuses = rule.trigger?.value?.pageStatuses;
	return (
		Array.isArray(statuses) &&
		statuses.some((status) => equalsIgnoreCase(status?.label, READY_FOR_REVIEW))
	);
};

const isConfluenceComponentType = (type: string | undefined): boolean =>
	typeof type === 'string' && type.toLowerCase().startsWith(CONFLUENCE_COMPONENT_TYPE_PREFIX);

/** True when the rule's trigger or any of its components is a Confluence component. */
const isConfluenceRule = (rule: StudioRuleEntry): boolean =>
	isConfluenceComponentType(rule.trigger?.type) ||
	(Array.isArray(rule.components) &&
		rule.components.some((component) => isConfluenceComponentType(component?.type)));

/** Returns the enabled rules that involve Confluence (via their trigger or any component). */
export const getRunningConfluenceRules = (rules: StudioRuleEntry[]): StudioRuleEntry[] =>
	rules.filter(
		(rule) => isRule(rule) && equalsIgnoreCase(rule.state, ENABLED_STATE) && isConfluenceRule(rule),
	);

const isFolderReference = (record: Record<string, unknown>, folderId: string): boolean =>
	equalsIgnoreCase(record.type as string | undefined, 'folder') &&
	record.id != null &&
	String(record.id) === folderId;

/** True when a `{ id, type: 'folder' }` reference to the folder appears in the value, at any depth. */
const configContainsFolderReference = (value: unknown, folderId: string): boolean => {
	if (Array.isArray(value)) {
		return value.some((item) => configContainsFolderReference(item, folderId));
	}
	if (value != null && typeof value === 'object') {
		const record = value as Record<string, unknown>;
		return (
			isFolderReference(record, folderId) ||
			Object.values(record).some((child) => configContainsFolderReference(child, folderId))
		);
	}
	return false;
};

/**
 * True when the rule's trigger or any of its (nested) components holds a `{ id, type: 'folder' }`
 * reference to the folder, e.g. a branch's `parent`.
 */
export const ruleReferencesFolder = (rule: StudioRuleEntry, folderId: string): boolean =>
	configContainsFolderReference(rule.trigger, folderId) ||
	configContainsFolderReference(rule.components, folderId);

/**
 * Returns `true` when any rule is a meeting-notes automation: it is triggered by a page publish, its
 * name/description contains "meeting notes", AND it sends a templated email. Rule state
 * (enabled/disabled) is ignored.
 */
export const existingMeetingNotesRuleExists = (rules: StudioRuleEntry[]): boolean =>
	rules.some(
		(rule) =>
			isRule(rule) &&
			hasTriggerType(rule, PAGE_PUBLISHED_TRIGGER_TYPE) &&
			`${rule.name ?? ''} ${rule.description ?? ''}`.toLowerCase().includes(MEETING_NOTES_TEXT) &&
			hasComponentOfType(rule.components, SEND_TEMPLATED_EMAIL_ACTION_TYPE),
	);

/**
 * Returns `true` when any rule is a keep-up-to-date automation for the folder: it has an inactive-page
 * branch AND an archive-page action (at any depth), AND it references the folder. Rule state
 * (enabled/disabled) is ignored.
 */
export const existingKeepUpToDateRuleExists = (
	rules: StudioRuleEntry[],
	folderId: string,
): boolean =>
	rules.some(
		(rule) =>
			isRule(rule) &&
			hasComponentOfType(rule.components, STALE_PAGE_BRANCH_TYPE) &&
			hasComponentOfType(rule.components, ARCHIVE_PAGE_ACTION_TYPE) &&
			ruleReferencesFolder(rule, folderId),
	);

/**
 * Returns `true` when any rule is a content-reviewer automation: it is triggered by a page-status
 * change to "Ready for review" AND it runs the Rovo agent (invoke-agent) action. Rule state
 * (enabled/disabled) is ignored.
 */
export const existingContentReviewerRuleExists = (rules: StudioRuleEntry[]): boolean =>
	rules.some(
		(rule) =>
			isRule(rule) &&
			triggersOnReadyForReview(rule) &&
			hasComponentOfType(rule.components, INVOKE_AGENT_ACTION_TYPE),
	);
